/* Customized reach tabs */
/* Documentation: https://reach.tech/tabs */
import '@reach/tabs/styles.css' // load base styles and a11y helpers
import {
  Tab as ReachTab,
  Tabs as ReachTabs,
  TabList as ReachTabList,
  TabPanel as ReachTabPanel,
  TabPanels as ReachTabPanels,
  useTabsContext as reachUseTabContext,
  TabProps,
  TabListProps,
  TabPanelsProps
} from '@reach/tabs'
import { Property } from 'csstype'
import React from 'react'

import theme from 'theme'

export const useTabsContext = reachUseTabContext

export const Tabs = ReachTabs
export const TabPanel = ReachTabPanel

export const TabList = React.forwardRef<HTMLDivElement, TabListProps>(
  (props, ref) => {
    return (
      <ReachTabList
        ref={ref}
        css={{
          position: 'relative',
          display: 'grid',
          gridAutoColumns: 'min-content',
          gridAutoFlow: 'column',
          gap: theme.space[3],
          whiteSpace: 'nowrap',
          overflow: 'auto',
          padding: 0,
          background: 'transparent',
          borderBottom: theme.border.base,

          '&[aria-orientation=vertical]': {
            flex: 'none',
            gridAutoColumns: 'none',
            gridAutoRows: 'min-content',
            gridAutoFlow: 'row',
            borderRight: theme.border.base,
            borderBottom: 0
          }
        }}
        {...props}
      />
    )
  }
)

TabList.displayName = 'TabList'

export const TabPanels = React.forwardRef<HTMLDivElement, TabPanelsProps>(
  (props, ref) => {
    const { selectedIndex } = useTabsContext()
    const filteredChildren = React.Children.map(props.children, (child, i) =>
      // if the tab is selected render it as normal, otherwise render an empty TabPanel to prevent
      // expensive content from rendering in background tabs
      i === selectedIndex ? child : <ReachTabPanel />
    )
    return (
      <ReachTabPanels ref={ref} {...props}>
        {filteredChildren}
      </ReachTabPanels>
    )
  }
)

TabPanels.displayName = 'TabPanels'

interface CustomTabProps extends TabProps {
  capitalize?: boolean
  color?: Property.Color
}

export const Tab = React.forwardRef<HTMLButtonElement, CustomTabProps>(
  ({ capitalize = true, color, disabled, ...rest }, ref) => {
    return (
      <ReachTab
        ref={ref}
        data-test-subtab
        css={{
          cursor: 'pointer',
          color: color || theme.colors.steel[1],
          opacity: disabled && theme.opacity[0],
          borderBottomStyle: 'solid',
          borderBottomColor: 'transparent',
          borderBottomWidth: theme.space[1],
          paddingBottom: theme.space[2],
          textTransform: capitalize ? 'capitalize' : 'none',
          transition: 'all 350ms ease',
          fontWeight: theme.fontWeights[1],

          '&[data-selected], &:hover': {
            color: color ? color : theme.colors.steel[2]
          },

          '&:active': {
            borderBottomColor: theme.colors.steel[2],
            opacity: 0.8,
            borderTopLeftRadius: theme.borderRadius[0],
            borderTopRightRadius: theme.borderRadius[0]
          },

          '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',

            '&:hover': {
              color: color ? color : theme.colors.steel[2]
            }
          },

          '&[data-orientation="vertical"]': {
            borderRadius: 0
          }
        }}
        disabled={disabled}
        {...rest}
      />
    )
  }
)

Tab.displayName = 'Tab'
